import { SourceAccountPicker } from './SourceAccountPicker';
import { ownerDocumentOf, ownerWindowOf } from '../../utils/ownerWindow';
import { useWideContent } from '../shared/PopoutWindow';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useMonthlyVersions } from '../../context/ForecastV3Context';
import { X, Plus, Trash2, GripVertical, DollarSign, Percent, Calendar, Info, Check, Search } from 'lucide-react';
import type { ZeroBaseDriverLayer, ZeroBaseLayerType, ForecastAccount } from '../../types';

// ─── Layer type configs ───
const LAYER_TYPES: Record<ZeroBaseLayerType, {
  label: string;
  tag: string;
  color: string;
  bgColor: string;
  textColor: string;
}> = {
  fixed: {
    label: 'Fixed Amount',
    tag: 'FIXED',
    color: 'var(--lodge-onyx-500)',
    bgColor: 'var(--lodge-onyx-50)',
    textColor: 'text-slate-700',
  },
  pct_revenue: {
    label: '% of Revenue',
    tag: '% REV',
    color: 'var(--color-success)',
    bgColor: 'var(--color-success-bg)',
    textColor: 'text-green-700',
  },
  pct_other: {
    label: '% of Account',
    tag: '% ACCT',
    color: 'var(--color-warning)',
    bgColor: 'var(--color-warning-bg)',
    textColor: 'text-amber-700',
  },
  por: {
    label: '$POR (per occupied room)',
    tag: '$POR',
    color: 'var(--lodge-orange-400)',
    bgColor: 'var(--lodge-orange-50)',
    textColor: 'text-orange-700',
  },
  par: {
    label: '$PAR (per available room)',
    tag: '$PAR',
    color: 'var(--lodge-orange-600, var(--fv3-accent-hover, #E8722A))',
    bgColor: 'var(--lodge-orange-50)',
    textColor: 'text-orange-700',
  },
};

interface ZeroBaseDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (layers: ZeroBaseDriverLayer[]) => void;
  initialLayers?: ZeroBaseDriverLayer[];
  accountName: string;
  accountNumber: string;
  department: string;
  accounts?: ForecastAccount[];  // Available accounts for "% of Account" source selection
  occupiedRoomsPerMonth?: Record<string, number>;  // e.g. { jan: 3100, feb: 2800, ... }
  availableRoomsPerMonth?: Record<string, number>;
}

function LayerTypeTag({ type }: { type: ZeroBaseLayerType }) {
  const cfg = LAYER_TYPES[type];
  if (!cfg) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide"
      style={{ background: cfg.bgColor, color: cfg.color }}
    >
      {cfg.tag}
    </span>
  );
}

function LayerTypeDropdown({
  value,
  onChange,
  disabled,
}: {
  value: ZeroBaseLayerType;
  onChange: (v: ZeroBaseLayerType) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const doc = ownerDocumentOf(ref.current);
    doc.addEventListener('mousedown', handler);
    return () => doc.removeEventListener('mousedown', handler);
  }, []);

  const selected = LAYER_TYPES[value];

  return (
    <div ref={ref} className="relative" style={{ width: 170 }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className="flex items-center justify-between gap-1.5 w-full px-2.5 py-[7px] bg-white border border-slate-200 rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={open ? { borderColor: 'var(--lodge-orange-400)', boxShadow: '0 0 0 2px var(--fv3-accent-tint, rgba(242,140,56,0.1))' } : {}}
      >
        <span className="flex items-center gap-1.5 text-left">
          <span style={{ color: selected.color }} className="flex items-center">
            {value === 'fixed' && <DollarSign size={13} />}
            {(value === 'pct_revenue' || value === 'pct_other') && <Percent size={13} />}
            {(value === 'por' || value === 'par') && <Calendar size={13} />}
          </span>
          <span className="text-slate-800 text-[13px]">{selected.label}</span>
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-slate-400"><path d="M3 4.5l3 3 3-3"/></svg>
      </button>
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 w-full z-50 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
          {(Object.entries(LAYER_TYPES) as [ZeroBaseLayerType, typeof LAYER_TYPES[ZeroBaseLayerType]][]).map(([key, cfg]) => (
            <button
              key={key}
              type="button"
              onClick={() => { onChange(key); setOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-[13px] text-left transition-colors hover:bg-slate-50"
              style={value === key ? { background: 'var(--fv3-accent-tint, rgba(242,140,56,0.06))' } : {}}
            >
              <span style={{ color: cfg.color }} className="flex items-center">
                {key === 'fixed' && <DollarSign size={13} />}
                {(key === 'pct_revenue' || key === 'pct_other') && <Percent size={13} />}
                {(key === 'por' || key === 'par') && <Calendar size={13} />}
              </span>
              {cfg.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatPreviewValue(n: number): string {
  return '$' + Math.round(n).toLocaleString();
}

export function ZeroBaseDriverModal({
  isOpen,
  onClose,
  onApply,
  initialLayers,
  accountName,
  accountNumber,
  department,
  accounts = [],
  occupiedRoomsPerMonth,
  availableRoomsPerMonth,
}: ZeroBaseDriverModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // In a popped-out pane, grow the window while this modal is open.
  useWideContent(isOpen, 980 + 120, 820);
  const { activeMonth } = useMonthlyVersions();
  const [layers, setLayers] = useState<ZeroBaseDriverLayer[]>(
    initialLayers && initialLayers.length > 0
      ? initialLayers
      : [{ id: 1, type: 'fixed', description: '', value: null, sourceChartOfAccountIds: [] }]
  );
  // Tracks raw string input per layer id so decimals aren't stripped while typing
  const [rawValues, setRawValues] = useState<Record<number, string>>({});
  // Tracks which layer's source-account picker is open (by layer id), and search text
  const [openPickerId, setOpenPickerId] = useState<number | null>(null);
  const nextId = useRef(
    initialLayers && initialLayers.length > 0
      ? Math.max(...initialLayers.map(l => l.id)) + 1
      : 2
  );

  // Reset layers when modal opens with new initial data
  useEffect(() => {
    if (isOpen) {
      if (initialLayers && initialLayers.length > 0) {
        setLayers(initialLayers);
        setRawValues({});
        setOpenPickerId(null);
        nextId.current = Math.max(...initialLayers.map(l => l.id)) + 1;
      } else {
        setLayers([{ id: 1, type: 'fixed', description: '', value: null, sourceChartOfAccountIds: [] }]);
        setRawValues({});
        setOpenPickerId(null);
        nextId.current = 2;
      }
    }
  }, [isOpen, initialLayers]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const doc = ownerDocumentOf(modalRef.current);
    if (isOpen) doc.addEventListener('keydown', handleKeyDown);
    return () => doc.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const addLayer = () => {
    setLayers(prev => [...prev, {
      id: nextId.current++,
      type: 'fixed',
      description: '',
      value: null,
      sourceChartOfAccountIds: [],
    }]);
  };

  const updateLayer = (id: number, updates: Partial<ZeroBaseDriverLayer>) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const removeLayer = (id: number) => {
    setLayers(prev => prev.filter(l => l.id !== id));
  };

  // Preview calculations — use real account data and real room stats for the active month
  const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

  // Active month key (e.g. 'apr' for month 4)
  const activeMonthKey = activeMonth != null ? MONTH_KEYS[activeMonth - 1] : null;

  const getMonthlyValue = (acct: ForecastAccount): number => {
    if (activeMonthKey) {
      // When we have an active month, always use that month's value (0 if missing — no fallback)
      return Math.abs(acct.monthlyValues[activeMonthKey] ?? 0);
    }
    // No active month — fall back to average across non-zero months
    const vals = MONTH_KEYS.map(k => acct.monthlyValues[k] ?? 0).filter(v => v !== 0);
    return vals.length > 0 ? vals.reduce((s, x) => s + Math.abs(x), 0) / vals.length : 0;
  };

  const estimatedMonthlyRevenue = useMemo(() =>
    accounts.filter(a => a.accountType === 'Revenue').reduce((sum, a) => sum + getMonthlyValue(a), 0) || 485000,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, activeMonthKey]
  );

  // Use actual room stats for the active month; fall back to estimates
  const previewOccupied = (activeMonthKey && occupiedRoomsPerMonth?.[activeMonthKey]) || 3420;
  const previewAvailable = (activeMonthKey && availableRoomsPerMonth?.[activeMonthKey]) || 4200;

  const getLayerMonthly = (layer: ZeroBaseDriverLayer): number => {
    const v = layer.value || 0;
    switch (layer.type) {
      case 'fixed': return v;
      case 'pct_revenue': return estimatedMonthlyRevenue * (v / 100);
      case 'por': return v * previewOccupied;
      case 'par': return v * previewAvailable;
      case 'pct_other': {
        const sourceIds = layer.sourceChartOfAccountIds || [];
        if (sourceIds.length === 0) return 0;
        const sourceTotal = sourceIds.reduce((sum, id) => {
          const acct = accounts.find(a => a.chartOfAccountId === id);
          return sum + (acct ? getMonthlyValue(acct) : 0);
        }, 0);
        return sourceTotal * (v / 100);
      }
      default: return 0;
    }
  };

  const totalMonthly = layers.reduce((sum, l) => sum + getLayerMonthly(l), 0);

  const handleApply = () => {
    onApply(layers);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div ref={modalRef} className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop — token scrim: strong enough to read as a dimmed layer in dark mode */}
      <div className="fv3-scrim absolute inset-0" style={{ background: 'var(--scrim, rgba(0,0,0,0.5))' }} onClick={onClose} />

      {/* Modal */}
      <div className="fv3-dialog relative bg-white rounded-lg shadow-xl border border-slate-200 w-full max-h-[90vh] flex flex-col overflow-hidden" style={{ maxWidth: 'min(980px, calc(100vw - 32px))' }}>
        {/* Header */}
        <div className="fv3-dialog-head px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-semibold text-slate-800" data-fv3-title="" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Zero Base Driver Configuration
              </h2>
              <span
                className="px-2 py-0.5 rounded text-[11px] font-semibold"
                style={{ background: 'var(--fv3-accent-tint, rgba(242,140,56,0.08))', color: 'var(--lodge-orange-500, var(--fv3-accent, #F28C38))' }}
              >
                {layers.length} LAYER{layers.length !== 1 ? 'S' : ''}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Stack multiple drivers to build a zero-based forecast for this account
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Account Context Bar */}
        <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-6 text-sm">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-400 font-medium uppercase tracking-wide">Account</span>
            <span className="text-[13px] text-slate-800 font-medium">{accountNumber} · {accountName}</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-400 font-medium uppercase tracking-wide">Dept</span>
            <span className="text-[13px] text-slate-800 font-medium">{department}</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-400 font-medium uppercase tracking-wide">Method</span>
            <span
              className="text-[11px] font-semibold px-2 py-0.5 rounded"
              style={{ background: 'var(--fv3-accent-tint, rgba(242,140,56,0.08))', color: 'var(--lodge-orange-500, var(--fv3-accent, #F28C38))' }}
            >
              MULTI-DRIVER (COMPOSABLE)
            </span>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto">
          {/* Column Headers */}
          <div className="flex gap-2.5 px-3.5 py-2 items-center border-b border-slate-100">
            <div className="w-3.5" /> {/* Grip spacer */}
            <span className="flex-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Description</span>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" style={{ width: 130 }}>Value</span>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" style={{ width: 180 }}>Context</span>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" style={{ width: 170 }}>Driver Type</span>
            <div className="w-6" /> {/* Delete spacer */}
          </div>

          {/* Driver Layer Rows */}
          {layers.map((layer, index) => {
            const isPct = layer.type === 'pct_revenue' || layer.type === 'pct_other';

            return (
              <div
                key={layer.id}
                className="flex items-center gap-2.5 px-3.5 py-3 border-b border-slate-100 transition-colors"
                style={{ background: index % 2 === 0 ? 'var(--surface-1, #fff)' : 'var(--surface-sunken, var(--fv3-n-50, #FAFBFC))' }}
              >
                {/* Drag handle */}
                <div className="text-slate-300 cursor-grab pt-1.5 flex-shrink-0">
                  <GripVertical size={14} />
                </div>

                {/* Description */}
                <div className="flex-1">
                  <input
                    value={layer.description}
                    onChange={e => updateLayer(layer.id, { description: e.target.value })}
                    placeholder="Line item description..."
                    className="w-full px-2.5 py-[7px] bg-white border border-slate-200 rounded-md text-[13px] text-slate-800 outline-none transition-colors focus:border-orange-400 focus:shadow-[0_0_0_2px_var(--fv3-accent-tint, rgba(242,140,56,0.1))]"
                  />
                </div>

                {/* Value input */}
                <div className="relative flex-shrink-0" style={{ width: 130 }}>
                  {!isPct && (
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[13px] pointer-events-none">$</span>
                  )}
                  <input
                    type="text"
                    value={rawValues[layer.id] !== undefined ? rawValues[layer.id] : (layer.value !== null ? String(layer.value) : '')}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^0-9.\-]/g, '');
                      setRawValues(prev => ({ ...prev, [layer.id]: raw }));
                      const parsed = parseFloat(raw);
                      updateLayer(layer.id, { value: raw === '' ? null : (isNaN(parsed) ? layer.value : parsed) });
                    }}
                    onBlur={e => {
                      const parsed = parseFloat(e.target.value);
                      setRawValues(prev => {
                        const next = { ...prev };
                        delete next[layer.id];
                        return next;
                      });
                      if (!isNaN(parsed)) updateLayer(layer.id, { value: parsed });
                    }}
                    placeholder="0.00"
                    className="w-full py-[7px] bg-white border border-slate-200 rounded-md text-[13px] text-slate-800 outline-none text-right transition-colors focus:border-orange-400 focus:shadow-[0_0_0_2px_var(--fv3-accent-tint, rgba(242,140,56,0.1))]"
                    style={{
                      paddingLeft: isPct ? 10 : 22,
                      paddingRight: isPct ? 26 : 10,
                    }}
                  />
                  {isPct && (
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[13px] pointer-events-none">%</span>
                  )}
                </div>

                {/* Context */}
                <div className="flex-shrink-0 relative" style={{ width: 180 }}>
                  {layer.type === 'pct_other' ? (() => {
                    const selectedIds = layer.sourceChartOfAccountIds || [];
                    const isOpen = openPickerId === layer.id;
                    return (
                      <>
                        {/* Trigger button */}
                        <button
                          type="button"
                          onClick={() => {
                            setOpenPickerId(isOpen ? null : layer.id);
                          }}
                          className="w-full flex items-center justify-between gap-1.5 px-2.5 py-[7px] bg-white border border-slate-200 rounded-md text-[13px] outline-none transition-colors hover:border-orange-400"
                          style={isOpen ? { borderColor: 'var(--fv3-accent, #f28c38)', boxShadow: '0 0 0 2px var(--fv3-accent-tint, rgba(242,140,56,0.1))' } : undefined}
                        >
                          <span className="truncate text-left" style={{ color: selectedIds.length ? 'var(--fv3-n-900, #1e293b)' : 'var(--fv3-n-400, #94a3b8)' }}>
                            {selectedIds.length === 0
                              ? 'Select accounts...'
                              : selectedIds.length === 1
                                ? (() => { const a = accounts.find(a => a.chartOfAccountId === selectedIds[0]); return a ? a.accountName : '1 account'; })()
                                : `${selectedIds.length} accounts`}
                          </span>
                          <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Popover */}
                        {isOpen && (
                          <>
                            {/* Backdrop to close on outside click */}
                            <div className="fixed inset-0 z-40" onClick={() => setOpenPickerId(null)} />
                            <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" style={{ width: 420 }}>
                              <div className="p-3" onClick={e => e.stopPropagation()}>
                                <SourceAccountPicker
                                  accounts={accounts}
                                  selectedIds={selectedIds}
                                  dense
                                  autoFocus
                                  maxListHeight={220}
                                  footerVerb="feed this layer"
                                  onChange={(newIds) => updateLayer(layer.id, { sourceChartOfAccountIds: newIds })}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    );
                  })() : (
                    <span className="text-xs text-slate-500 flex items-center gap-1 h-full py-[7px]">
                      {layer.type === 'pct_revenue' && <><Info size={12} className="text-green-500 flex-shrink-0" /> of Total Revenue</>}
                      {layer.type === 'por' && <><Info size={12} style={{ color: 'var(--fv3-accent, #F28C38)' }} className="flex-shrink-0" /> × Occupied Rooms / night</>}
                      {layer.type === 'par' && <><Info size={12} style={{ color: 'var(--fv3-accent, #F28C38)' }} className="flex-shrink-0" /> × Available Rooms / night</>}
                      {layer.type === 'fixed' && <><Info size={12} className="text-blue-500 flex-shrink-0" /> per month</>}
                    </span>
                  )}
                </div>

                {/* Driver Type dropdown */}
                <div className="flex-shrink-0">
                  <LayerTypeDropdown
                    value={layer.type}
                    onChange={type => updateLayer(layer.id, { type })}
                  />
                </div>

                {/* Delete button */}
                <button
                  onClick={() => removeLayer(layer.id)}
                  className="flex-shrink-0 p-1.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}

          {/* Add Layer Button */}
          <div className="px-3.5 py-3 border-b border-slate-100">
            <button
              onClick={addLayer}
              className="flex items-center gap-1.5 justify-center w-full px-3.5 py-2 border border-dashed border-slate-200 rounded-md text-[13px] text-slate-500 transition-all hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50/50"
            >
              <Plus size={14} /> Add Driver Layer
            </button>
          </div>

          {/* Preview Section */}
          <div className="border-t border-slate-200 px-5 py-4 bg-slate-50">
            <div className="flex justify-between items-center mb-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Forecast Preview · Monthly Estimate
              </span>
              <div className="flex gap-8">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-[90px] text-right">Monthly</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-[100px] text-right">Annualized</span>
              </div>
            </div>

            <div className="bg-white rounded-md border border-slate-200 px-4 py-3">
              <div className="text-[10px] text-slate-400 mb-1.5 italic">
                {layers.some(l => l.type === 'por' || l.type === 'par') && !occupiedRoomsPerMonth
                  ? 'Revenue & account values from current forecast; room counts are estimated'
                  : `Based on ${activeMonthKey ? activeMonthKey.charAt(0).toUpperCase() + activeMonthKey.slice(1) + ' ' : ''}current forecast values`}
              </div>

              {/* Individual layer rows */}
              {layers.map(layer => {
                const cfg = LAYER_TYPES[layer.type];
                const monthly = getLayerMonthly(layer);
                return (
                  <div key={layer.id} className="flex justify-between items-center py-1.5 text-[13px] text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: cfg.color }} />
                      {layer.description || '(untitled)'}
                    </span>
                    <div className="flex gap-8">
                      <span className="w-[90px] text-right">{formatPreviewValue(monthly)}</span>
                      <span className="w-[100px] text-right">{formatPreviewValue(monthly * 12)}</span>
                    </div>
                  </div>
                );
              })}

              {/* Total row */}
              <div className="flex justify-between items-center pt-2.5 mt-1 border-t border-slate-200 text-[13px] font-semibold text-slate-800">
                <span>Total Forecast</span>
                <div className="flex gap-8">
                  <span className="w-[90px] text-right">{formatPreviewValue(totalMonthly)}</span>
                  <span className="w-[100px] text-right">{formatPreviewValue(totalMonthly * 12)}</span>
                </div>
              </div>
            </div>

            {/* Stacked bar */}
            <div className="mt-3">
              <div className="flex h-2 rounded overflow-hidden bg-slate-100">
                {layers.map(layer => {
                  const monthly = getLayerMonthly(layer);
                  const pct = totalMonthly > 0 ? (monthly / totalMonthly) * 100 : 0;
                  const cfg = LAYER_TYPES[layer.type];
                  return (
                    <div
                      key={layer.id}
                      style={{ width: `${pct}%`, background: cfg.color, transition: 'width 0.3s ease' }}
                      title={`${layer.description || '(untitled)'}: ${formatPreviewValue(monthly)} (${Math.round(pct)}%)`}
                    />
                  );
                })}
              </div>
              <div className="flex gap-4 mt-2 flex-wrap">
                {layers.map(layer => {
                  const cfg = LAYER_TYPES[layer.type];
                  const monthly = getLayerMonthly(layer);
                  const pct = totalMonthly > 0 ? (monthly / totalMonthly) * 100 : 0;
                  return (
                    <span key={layer.id} className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: cfg.color }} />
                      <LayerTypeTag type={layer.type} />
                      <span>{Math.round(pct)}%</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fv3-dialog-foot px-5 py-3.5 border-t border-slate-200 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            Changes auto-propagate to all forecast months
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 rounded-md text-[13px] font-medium text-slate-500 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-5 py-2 rounded-md text-[13px] font-semibold text-white flex items-center gap-1.5 transition-colors"
              style={{
                background: 'var(--lodge-orange-500, var(--fv3-accent, #F28C38))',
                boxShadow: '0 1px 3px var(--fv3-accent-shadow, rgba(242,140,56,0.3))',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-orange-600, var(--fv3-accent-hover, #E8722A))')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--lodge-orange-500, var(--fv3-accent, #F28C38))')}
            >
              <Check size={14} /> Apply Drivers
            </button>
          </div>
        </div>

        {/* Contextual Tip */}
        <div className="mx-5 mb-4 px-4 py-2.5 bg-blue-50/80 rounded-md border border-blue-100 flex items-start gap-2">
          <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-blue-800 leading-relaxed">
            <strong>Multi-driver mode</strong> combines all layers additively to produce your forecast.
            Each layer calculates independently — fixed amounts stay constant, variable drivers recalculate
            when occupancy or revenue assumptions change.
          </span>
        </div>
      </div>
    </div>
  );
}
